import React, { FC, useContext, memo } from 'react';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import { useMutation } from '@apollo/client';

import TimelineSong from '../../TimelineSong';
import List, { DragResponse } from 'vibo-ui/List';
import { EventContext } from 'components/context/EventContext';
import { TimelineSongsContext } from '../../TimelineSongsContext';
import { AllTimelineGqlOptions, useTimeline } from 'components/events/Timeline/TimelineContext';

import { onError } from 'graphql/helpers';
import { hasSectionSongsFilterValue } from 'services/songs/helpers';

import { SongsViewProps } from '../interfaces';

import useStyles from '../style';

const SongsDjView: FC<SongsViewProps> = ({ className }) => {
  const classes = useStyles();

  const { songsOptions, section, refetchSongs } = useContext(TimelineSongsContext);
  const { isEventHost, isEventDj } = useContext(EventContext);
  const {
    variables,
    songs: { mutations },
  } = useTimeline<AllTimelineGqlOptions>();

  const [reorderSongs] = useMutation(mutations.reorder.mutation, {
    onCompleted: () => {
      refetchSongs();
    },
    onError,
  });

  const isReorderingEnabled =
    !songsOptions.songsSort &&
    (isEventDj || (isEventHost && section.settings.canHostsOrderSongs)) &&
    !songsOptions.isFilterApplied;

  const handleDragChange = (
    reorderedSongs: SectionSong[],
    { removedIndex, addedIndex, selectedIds }: DragResponse
  ) => {
    if (hasSectionSongsFilterValue(songsOptions.songsFilter) || songsOptions.songsSort !== null) {
      songsOptions.setSongsList([...songsOptions.songsList]);
      return;
    }
    if (!isEqual(reorderedSongs, songsOptions.songsList)) {
      const sourceSongIds = selectedIds?.length
        ? selectedIds
        : songsOptions.songsList[removedIndex]?._id;
      songsOptions.setSongsList(reorderedSongs);
      reorderSongs({
        variables: {
          ...variables,
          sourceSongIds,
          targetSongId: addedIndex < 0 ? null : songsOptions.songsList[addedIndex]?._id,
        },
      });
    }
  };

  return (
    <List<SectionSong>
      renderItem={(song, index) => (
        <TimelineSong
          song={song}
          index={index}
          className={classNames('timelineSong', song.viboSongId)}
          data-gtm-target-id="timeline-section-details-songs-full-song"
        />
      )}
      className={classNames('songsList', classes.songsDjView, className)}
      data={songsOptions.songsList}
      onDropCallback={handleDragChange}
      draggable={isReorderingEnabled}
      dragByHandler
    />
  );
};

export default memo(SongsDjView, () => true);
